import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface TopDrawerProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

// A top sheet that slides down from the top: backdrop + a TRANSPARENT slide
// container (Esc + tap-outside close, body scroll lock). The container itself
// has no surface — callers drop discontinuous glass cards inside, so the dimmed
// backdrop shows through the gaps between them (isolated islands rather than one
// continuous panel). Portaled to <body> to escape the chrome's backdrop-filter
// containing block. Provides only a floating close control.
const TopDrawer = ({ open, onClose, ariaLabel, children }: TopDrawerProps) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        onClick={onClose}
        aria-hidden
        className={
          "tw:fixed tw:inset-0 tw:z-50 tw:bg-black/30 tw:backdrop-blur-md tw:transition-opacity tw:duration-200 " +
          (open ? "tw:opacity-100" : "tw:opacity-0 tw:pointer-events-none")
        }
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={
          "tw:fixed tw:left-3 tw:right-3 tw:top-0 tw:z-50 tw:max-h-[90vh] tw:overflow-y-auto tw:transition-transform tw:duration-200 tw:ease-out " +
          (open ? "tw:translate-y-0" : "tw:-translate-y-full")
        }
      >
        {/* Transparent column of discontinuous islands (space-y gaps reveal the
            backdrop between them). Closes via tap-outside / Esc. */}
        <div className="tw:mx-auto tw:w-full tw:max-w-md tw:space-y-2 tw:py-3">
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default TopDrawer;
